import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { trackStoryGenerated } from '../lib/analytics';
import { ApiError } from '../lib/api';
import { type Hero, listHeroes } from '../lib/heroes';
import { inferAgeGroup } from '../lib/heroAge';
import {
  contactSupport,
  type CreateStoryPayload,
  createStory,
  fetchStoryOptions,
  type StoryListItem,
  type StoryOptions,
} from '../lib/stories';
import { CapacityReachedPanel } from '../components/wizard/CapacityReachedPanel';
import { AddHeroStep } from '../components/wizard/AddHeroStep';
import { HeroPickerStep } from '../components/wizard/HeroPickerStep';
import { LayersStep } from '../components/wizard/LayersStep';
import { PromptStep } from '../components/wizard/PromptStep';
import '../components/wizard/wizard.css';

const DEFAULT_ART_STYLE = 'paper_collage';
const DEFAULT_AGE_GROUP = '4_5';
const DEFAULT_MOOD = 'adventurous';
const DEFAULT_GENRE = 'auto';

type WizardStep = 'heroes' | 'add-hero' | 'prompt' | 'layers';

type WizardState = {
  step: WizardStep;
  heroes: Hero[];
  selectedHeroIds: Set<number>;
  prompt: string;
  artStyle: string;
  ageGroup: string;
  mood: string;
  genre: string;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; options: StoryOptions }
  | { kind: 'unauthenticated' }
  | { kind: 'error'; message: string };

const STEP_ORDER: Exclude<WizardStep, 'add-hero'>[] = ['heroes', 'prompt', 'layers'];

export function StoryNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [state, setState] = useState<WizardState>({
    step: 'heroes',
    heroes: [],
    selectedHeroIds: new Set(),
    prompt: '',
    artStyle: DEFAULT_ART_STYLE,
    ageGroup: DEFAULT_AGE_GROUP,
    mood: DEFAULT_MOOD,
    genre: DEFAULT_GENRE,
  });
  const ageGroupTouched = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [capacityErrorCode, setCapacityErrorCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listHeroes(), fetchStoryOptions()])
      .then(([heroes, options]) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, heroes }));
        setLoad({ kind: 'ready', options });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setLoad({ kind: 'unauthenticated' });
          return;
        }
        const message = err instanceof Error ? err.message : t('common.unknownError');
        setLoad({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  function toggleHero(id: number) {
    setState((prev) => {
      const next = new Set(prev.selectedHeroIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, selectedHeroIds: next };
    });
  }

  function handleHeroSaved(newHero: Hero) {
    setState((prev) => ({
      ...prev,
      step: 'heroes',
      heroes: [newHero, ...prev.heroes.filter((h) => h.id !== newHero.id)],
      selectedHeroIds: new Set([...prev.selectedHeroIds, newHero.id]),
    }));
  }

  function goNextFromHeroes() {
    setState((prev) => ({ ...prev, step: 'prompt' }));
  }

  function goNextFromPrompt() {
    if (load.kind !== 'ready') return;
    setState((prev) => {
      if (ageGroupTouched.current) {
        return { ...prev, step: 'layers' };
      }
      const selected = prev.heroes.filter((h) => prev.selectedHeroIds.has(h.id));
      const inferred = inferAgeGroup(selected, load.options.age_groups);
      return {
        ...prev,
        step: 'layers',
        ageGroup: inferred ?? prev.ageGroup,
      };
    });
  }

  function handleLayerChange(field: 'artStyle' | 'ageGroup' | 'mood' | 'genre', value: string) {
    if (field === 'ageGroup') ageGroupTouched.current = true;
    setState((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    const payload: CreateStoryPayload = {
      prompt: state.prompt.trim(),
      art_style: state.artStyle,
      age_group: state.ageGroup,
      mood: state.mood,
      genre: state.genre,
      hero_ids: [...state.selectedHeroIds],
    };
    try {
      const created = await createStory(payload);
      trackStoryGenerated(created.id, {
        art_style: payload.art_style,
        age_group: payload.age_group,
        mood: payload.mood,
        genre: payload.genre,
        hero_count: payload.hero_ids.length,
      });
      const stub: StoryListItem = {
        id: created.id,
        title: '',
        synopsis: '',
        status: created.status,
        error_message: '',
        art_style: state.artStyle,
        age_group: state.ageGroup,
        mood: state.mood,
        genre: state.genre,
        created_at: new Date().toISOString(),
        heroes: [],
      };
      navigate('/stories', { state: { optimisticStory: stub } });
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        const body = err.body as { detail?: string } | null;
        setSubmitError(body?.detail ?? t('stories.notEnoughTokens'));
      } else if (err instanceof ApiError && err.status === 503) {
        const body = err.body as { code?: string } | null;
        setCapacityErrorCode(body?.code ?? 'daily_capacity_reached');
      } else {
        setSubmitError(err instanceof Error ? err.message : t('stories.somethingWentWrong'));
      }
      setSubmitting(false);
    }
  }

  const visibleStepIndex = useMemo(() => {
    if (state.step === 'add-hero') return 0;
    return STEP_ORDER.indexOf(state.step);
  }, [state.step]);

  return (
    <>
      <WizardHeader
        stepIndex={visibleStepIndex}
        totalSteps={STEP_ORDER.length}
        onCancel={() => navigate('/stories')}
      />
      {capacityErrorCode && (
        <CapacityReachedPanel
          errorCode={capacityErrorCode}
          onDismiss={() => setCapacityErrorCode(null)}
          onContact={(message) => contactSupport(capacityErrorCode, message)}
        />
      )}
      <Body
        load={load}
        state={state}
        onToggleHero={toggleHero}
        onAddHero={() => setState((p) => ({ ...p, step: 'add-hero' }))}
        onCancelAddHero={() => setState((p) => ({ ...p, step: 'heroes' }))}
        onHeroSaved={handleHeroSaved}
        onNextFromHeroes={goNextFromHeroes}
        onBackFromPrompt={() => setState((p) => ({ ...p, step: 'heroes' }))}
        onNextFromPrompt={goNextFromPrompt}
        onPromptChange={(prompt) => setState((p) => ({ ...p, prompt }))}
        onBackFromLayers={() => setState((p) => ({ ...p, step: 'prompt' }))}
        onLayerChange={handleLayerChange}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitError={submitError}
      />
    </>
  );
}

function WizardHeader({
  stepIndex,
  totalSteps,
  onCancel,
}: {
  stepIndex: number;
  totalSteps: number;
  onCancel: () => void;
}) {
  return (
    <header className="wizard-header">
      <div className="wizard-header__dots" aria-hidden="true">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span
            key={i}
            className={`wizard-header__dot${
              i === stepIndex
                ? ' wizard-header__dot--current'
                : i < stepIndex
                  ? ' wizard-header__dot--done'
                  : ''
            }`}
          />
        ))}
      </div>
      <CancelButton onCancel={onCancel} />
    </header>
  );
}

function CancelButton({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="wizard-header__cancel" onClick={onCancel}>
      {t('common.cancel')}
    </button>
  );
}

type BodyProps = {
  load: LoadState;
  state: WizardState;
  onToggleHero: (id: number) => void;
  onAddHero: () => void;
  onCancelAddHero: () => void;
  onHeroSaved: (h: Hero) => void;
  onNextFromHeroes: () => void;
  onBackFromPrompt: () => void;
  onNextFromPrompt: () => void;
  onPromptChange: (v: string) => void;
  onBackFromLayers: () => void;
  onLayerChange: (field: 'artStyle' | 'ageGroup' | 'mood' | 'genre', value: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  submitError: string | null;
};

function Body(props: BodyProps) {
  const { t } = useTranslation();
  if (props.load.kind === 'loading') {
    return <p className="wizard-status">{t('common.loading')}</p>;
  }
  if (props.load.kind === 'unauthenticated') {
    return (
      <p className="wizard-status">
        <Trans i18nKey="common.notSignedIn" components={[<a href="/" key="0" />]} />
      </p>
    );
  }
  if (props.load.kind === 'error') {
    return (
      <p className="wizard-status wizard-status--error">
        {t('common.error', { message: props.load.message })}
      </p>
    );
  }

  const { state } = props;
  const { options } = props.load;

  return (
    <div className="wizard-body">
      <div className="wizard-step-anim" key={state.step}>
        {state.step === 'heroes' && (
          <HeroPickerStep
            heroes={state.heroes}
            selectedIds={state.selectedHeroIds}
            onToggle={props.onToggleHero}
            onAddHero={props.onAddHero}
            onNext={props.onNextFromHeroes}
            onBack={() => window.history.back()}
          />
        )}
        {state.step === 'add-hero' && (
          <AddHeroStep onSaved={props.onHeroSaved} onCancel={props.onCancelAddHero} />
        )}
        {state.step === 'prompt' && (
          <PromptStep
            value={state.prompt}
            onChange={props.onPromptChange}
            onNext={props.onNextFromPrompt}
            onBack={props.onBackFromPrompt}
          />
        )}
        {state.step === 'layers' && (
          <LayersStep
            options={options}
            artStyle={state.artStyle}
            ageGroup={state.ageGroup}
            mood={state.mood}
            genre={state.genre}
            onChange={props.onLayerChange}
            onSubmit={props.onSubmit}
            onBack={props.onBackFromLayers}
            submitting={props.submitting}
            error={props.submitError}
          />
        )}
      </div>
    </div>
  );
}
